import React, {useEffect, useState, useCallback} from "react"
import {RouteComponentProps} from "react-router-dom"
import {Grid} from "react-bootstrap"

import {WellnessMedia, WellnessMediaType, MarketingProfile, UpdateMarketingProfileInput, QueryGetWellnessMediaByIdArgs} from "@irati/types"

import Loader from "@irati/ui/src/Loading/Loader"
import marketingService from "@irati/marketing/services/marketing"
import useAuth from "@irati/auth/helpers/auth/useAuth"

import {createUseQuery} from "@irati/utils/services/apollo"
import {getWellnessMediaByIdKey, getWellnessMediaByIdQuery} from "@irati/wellness/services/queries/getWellnessMediaById"

import SingleMedia from "./SingleMedia"
import CourseMedia from "./CourseMedia"
import {NotFound} from "./NotFound"

interface Match {
  mediaId: string
}

type Props = RouteComponentProps<Match>

const useMediaDetail = createUseQuery<WellnessMedia, QueryGetWellnessMediaByIdArgs>(getWellnessMediaByIdQuery, {
  key: getWellnessMediaByIdKey
})

const MediaDetail: React.FC<Props> = ({match}) => {
  const {mediaId} = match.params
  const user = useAuth()?.user?.uuid
  const [marketingProfile, setMarketingProfile] = useState<MarketingProfile>()
  const [profileLoading, setProfileLoading] = useState(true)

  const {loading, errorMsg, data: media} = useMediaDetail({mediaId})

  useEffect(() => {
    if (!mediaId || !user) return
    setProfileLoading(true)

    marketingService
      .getProfile({userId: user})
      .then((profile) => setMarketingProfile(profile))
      .finally(() => setProfileLoading(false))
  }, [mediaId, user])

  const handleClick = useCallback(
    (marketingProfileInput: UpdateMarketingProfileInput) => {
      marketingService.updateProfile(marketingProfileInput)
      if (!user) return
      marketingService.getProfile({userId: user}).then((updatedProfile) => {
        setMarketingProfile(updatedProfile)
      })
    },
    [user]
  )

  if (loading || profileLoading) return <Loader />
  if (errorMsg || media == null)
    return (
      <Grid>
        <NotFound />
      </Grid>
    )

  const showSingleScreen =
    media?.type === WellnessMediaType.SingleVideo ||
    media?.type === WellnessMediaType.SingleAudio ||
    media?.type === WellnessMediaType.LiveVideo ||
    media?.type === WellnessMediaType.Article

  const showCourseScreen = media?.type === WellnessMediaType.VideoCourse || media?.type === WellnessMediaType.AudioCourse

  return (
    <>
      {showCourseScreen && <CourseMedia media={media} />}
      {showSingleScreen && marketingProfile && <SingleMedia media={media} marketingProfile={marketingProfile} handleClick={handleClick} />}
      {!showCourseScreen && !showSingleScreen && (
        <Grid>
          <NotFound />
        </Grid>
      )}
    </>
  )
}

export default MediaDetail
